import type { BodyUUID, Joint, JointKind, JointUUID } from "../../types/scene";
import { create } from "mutative";
import type { SceneStoreGetState, SceneStoreSetState } from "../sceneStore.types";
import type { JointActions, JointInput } from "./actionTypes";
import { logger } from "../../utils/logger";
import { shouldBlockPhysicsMutation } from "../../utils/physicsGuards";
import { namesFromRecord, nextUniqueName } from "../../utils/uniqueNames";
import { withHistory } from "./withHistory";
import {
  buildJointMap,
  createJointRecord,
  embedJointsIntoBodies,
  findHostBodyUuidForJoint,
  findParentBodyUuid,
  getDefaultJointName,
  hasBallRotationalConflict,
  hasOtherFreeJointOnBody,
  sanitizeJointAxis,
  sanitizeJointNonNegative,
  sanitizeJointPosition,
  shouldAutoRenameJointName,
  warnBallRotationalConflict,
  type ActuatorRecordByString,
  type BodyMapWithLegacyJointRefs,
  type BodyWithLegacyJointRefs,
  type EqualityRecordByString,
  type JointEquality,
  type JointKindUpdate,
  type JointMap,
  type JointRecordByString,
  type LegacyJointRef,
  type ScalarJointInput,
} from "./jointActionUtils";

type JointManagementActionKeys =
  | "setJoints"
  | "removeJoint"
  | "updateJointKind"
  | "setJointName"
  | "updateJointName"
  | "clearJoints"
  | "moveJointToBody"
  | "addJoint";

export const createJointManagementActions = (
  set: SceneStoreSetState,
  get: SceneStoreGetState
): Pick<JointActions, JointManagementActionKeys> => ({
  setJoints: (input: Joint[] | JointMap) => {
    if (shouldBlockPhysicsMutation("setJoints")) return;
    const jointMap: JointMap = buildJointMap(Array.isArray(input) ? input : input || {});

    set((state) =>
      create(state, (draft) => {
        draft.joints = jointMap;
        // Direct access to flat bodies map - no tree reconstruction needed
        embedJointsIntoBodies(draft.bodies, jointMap);
      })
    );
  },

  removeJoint: withHistory((jointUuid: JointUUID) => {
    if (shouldBlockPhysicsMutation("removeJoint")) return;
    set((state) =>
      create(state, (draft) => {
        if (!jointUuid) return;
        const joints = draft.joints as JointRecordByString;
        const actuators = draft.actuators as ActuatorRecordByString;
        const equality = draft.equality as EqualityRecordByString;
        const joint = joints[jointUuid];
        if (!joint) return;

        // Remove joint references from all bodies.
        Object.values(draft.bodies as BodyMapWithLegacyJointRefs).forEach((body) => {
          if (!Array.isArray(body?.joints)) return;
          body.joints = body.joints.filter((entry) => {
            if (typeof entry === "string") return entry !== jointUuid;
            if (entry && typeof entry === "object" && typeof entry.uuid === "string") {
              return entry.uuid !== jointUuid;
            }
            return true;
          });
        });

        // Remove actuators targeting this joint (by UUID or by joint name).
        Object.entries(actuators).forEach(([actuatorUuid, actuator]) => {
          if (!actuator) return;
          if (actuator.joint === jointUuid || actuator.joint === joint.name) {
            delete actuators[actuatorUuid];
          }
        });

        // Remove joint equality constraints referencing this joint name.
        Object.entries(equality).forEach(([eqUuid, eq]) => {
          if (!eq || eq.type !== "joint") return;
          const jointEquality = eq as JointEquality;
          if (jointEquality.name1 === joint.name || jointEquality.name2 === joint.name) {
            delete equality[eqUuid];
          }
        });

        delete joints[jointUuid];
      })
    );
  }),

  updateJointKind: withHistory((jointUuid: string, kind: JointKind) => {
    let renamedJointName: string | null = null;
    set((state) =>
      create(state, (draft) => {
        const joints = draft.joints as JointRecordByString;
        const joint = joints[jointUuid];
        if (!joint) return;
        const hostBodyUuid = findHostBodyUuidForJoint(draft.bodies, jointUuid);
        if (!hostBodyUuid) return;

        // MuJoCo free joints are valid only on root bodies and cannot share a body with other joints.
        if (kind === "free") {
          const parentBodyUuid = findParentBodyUuid(draft.bodies, hostBodyUuid);
          const bodyJointRefs = Array.isArray(draft?.bodies?.[hostBodyUuid]?.joints)
            ? draft.bodies[hostBodyUuid].joints
            : [];
          const otherJointCount = bodyJointRefs.filter((entry: LegacyJointRef) => {
            const refUuid = typeof entry === "string" ? entry : entry?.uuid;
            return !!refUuid && refUuid !== jointUuid;
          }).length;
          if (parentBodyUuid || otherJointCount > 0) {
            logger.warn(
              "[jointActions] Rejected free-joint conversion: free joints require a root body with no sibling joints."
            );
            return;
          }
        } else if (hasOtherFreeJointOnBody(draft, hostBodyUuid, jointUuid)) {
          logger.warn(
            "[jointActions] Rejected joint kind change: non-free joints cannot coexist with a free joint on the same body."
          );
          return;
        }
        if (hasBallRotationalConflict(draft.joints, draft.bodies, hostBodyUuid, kind, jointUuid)) {
          warnBallRotationalConflict("joint kind change", kind);
          return;
        }

        const updated: JointKindUpdate = { ...joint, kind };
        // Ensure kind-specific required fields are present with sensible defaults
        if (kind === "ball") {
          if (!Array.isArray(updated.position)) updated.position = [0, 0, 0];
          if (typeof updated.max_rotation !== "number") updated.max_rotation = Math.PI;
        } else if (kind === "hinge" || kind === "slide") {
          if (!Array.isArray(updated.axis)) updated.axis = [0, 1, 0];
          if (!Array.isArray(updated.range) || updated.range.length !== 2)
            updated.range = kind === "slide" ? [0, 1] : [-Math.PI, Math.PI];
          if (kind !== joint.kind && kind === "slide") updated.useLimits = "false";
          if (!Array.isArray(updated.position)) updated.position = [0, 0, 0];
        }
        if (kind !== joint.kind && shouldAutoRenameJointName(joint.name)) {
          const currentName = String(joint.name || "").trim();
          const existingNames = namesFromRecord(draft.joints).filter(
            (name: string) => String(name || "").trim() !== currentName
          );
          const nextName = nextUniqueName(getDefaultJointName(kind), existingNames);
          if (nextName !== currentName) {
            updated.name = nextName;
            renamedJointName = nextName;
          }
        }
        joints[jointUuid] = updated as Joint;
      })
    );

    if (renamedJointName) {
      try {
        window.dispatchEvent(
          new CustomEvent("joint-name-updated", {
            detail: { jointUuid, name: renamedJointName },
          })
        );
      } catch (error) {
        logger.warn("[jointActions] Failed to dispatch joint-name-updated event", error);
      }
    }
  }),

  setJointName: withHistory((jointUuid: string, name: string) => {
    set((state) =>
      create(state, (draft) => {
        const joints = draft.joints as JointRecordByString;
        const joint = joints[jointUuid];
        if (!joint) return;
        joints[jointUuid] = { ...joint, name };
      })
    );

    try {
      window.dispatchEvent(new CustomEvent("joint-name-updated", { detail: { jointUuid, name } }));
    } catch (error) {
      logger.warn("[jointActions] Failed to dispatch joint-name-updated event", error);
    }
  }),

  updateJointName: (jointUuid: string, name: string) => {
    get().actions.setJointName(jointUuid, name);
  },

  clearJoints: () => {
    set((state) =>
      create(state, (draft) => {
        draft.joints = {};
        // Clear joint references from all bodies (direct flat map access)
        Object.values(draft.bodies).forEach((body) => {
          if (body) body.joints = [];
        });
      })
    );
  },

  moveJointToBody: withHistory((jointUuid: JointUUID, targetBodyUuid: BodyUUID) => {
    set((state) =>
      create(state, (draft) => {
        if (!jointUuid || !targetBodyUuid) return;

        const joint = draft.joints[jointUuid];
        if (!joint) return;
        const currentParentBodyUuid = findHostBodyUuidForJoint(draft.bodies, jointUuid);
        if (!currentParentBodyUuid) return;
        const targetBody = draft.bodies[targetBodyUuid] || null;
        if (!targetBody) return;

        if (targetBodyUuid !== currentParentBodyUuid) {
          if (joint.kind === "free") {
            const parentOfTarget = findParentBodyUuid(draft.bodies, targetBodyUuid);
            const targetJointRefs = Array.isArray(targetBody.joints) ? targetBody.joints : [];
            if (parentOfTarget || targetJointRefs.length > 0) {
              logger.warn(
                "[jointActions] Rejected joint move: free joints require a root body with no sibling joints."
              );
              return;
            }
          } else if (hasOtherFreeJointOnBody(draft, targetBodyUuid)) {
            logger.warn(
              "[jointActions] Rejected joint move: non-free joints cannot coexist with a free joint on the same body."
            );
            return;
          }

          if (hasBallRotationalConflict(draft.joints, draft.bodies, targetBodyUuid, joint.kind)) {
            warnBallRotationalConflict("joint move", joint.kind);
            return;
          }
        }

        // Find current parent body in flat map
        let currentParentBody: BodyWithLegacyJointRefs | null = null;
        let jointIndex = -1;

        for (const body of Object.values(draft.bodies as BodyMapWithLegacyJointRefs)) {
          if (Array.isArray(body.joints)) {
            const idx = body.joints.findIndex((entry) => {
              if (typeof entry === "string") {
                return entry === jointUuid;
              }
              if (entry && typeof entry === "object") {
                if (entry.uuid && entry.uuid === jointUuid) return true;
                if (entry.name && entry.name === joint.name) return true;
              }
              return false;
            });
            if (idx !== -1) {
              currentParentBody = body;
              jointIndex = idx;
              break;
            }
          }
        }

        if (!currentParentBody || jointIndex === -1) return;
        const currentParentJoints = currentParentBody.joints;
        if (!Array.isArray(currentParentJoints)) return;

        // Remove from current parent
        const [storedEntry] = currentParentJoints.splice(jointIndex, 1) || [];
        if (storedEntry === undefined) return;
        const entryToStore =
          typeof storedEntry === "string" && storedEntry ? (storedEntry as JointUUID) : jointUuid;

        targetBody.joints = Array.isArray(targetBody.joints) ? targetBody.joints : [];
        targetBody.joints.push(entryToStore);
      })
    );
  }),

  addJoint: withHistory((jointInput: JointInput, parentBodyUuid: BodyUUID): JointUUID | null => {
    const normalizedInput: JointInput = { ...jointInput };
    normalizedInput.name = nextUniqueName(
      normalizedInput.name || `joint_${normalizedInput.kind || "hinge"}`,
      namesFromRecord(get().joints)
    );
    if (normalizedInput.kind === "free") {
      const state = get();
      const parentOfTarget = findParentBodyUuid(state.bodies, parentBodyUuid);
      const targetBody = state.bodies?.[parentBodyUuid];
      const hasExistingJoints = Array.isArray(targetBody?.joints) && targetBody.joints.length > 0;
      if (parentOfTarget || hasExistingJoints) {
        logger.error(
          "[jointActions] Rejected free-joint add: free joints require a root body with no existing joints."
        );
        return null;
      }
    }
    const state = get();
    if (
      hasBallRotationalConflict(state.joints, state.bodies, parentBodyUuid, normalizedInput.kind)
    ) {
      warnBallRotationalConflict("joint add", normalizedInput.kind);
      return null;
    }
    if (normalizedInput.kind !== "free" && hasOtherFreeJointOnBody(state, parentBodyUuid)) {
      logger.warn(
        "[jointActions] Rejected joint add: non-free joints cannot coexist with a free joint on the same body."
      );
      return null;
    }
    // Guard hinge/slide joints: axis must be non-zero, damping/frictionloss/stiffness must be >= 0.
    if (normalizedInput.kind === "hinge" || normalizedInput.kind === "slide") {
      const scalarInput = normalizedInput as ScalarJointInput;
      const safeAxis = sanitizeJointAxis(scalarInput.axis);
      scalarInput.axis = safeAxis ?? [0, 0, 1];
      scalarInput.position = sanitizeJointPosition(scalarInput.position);
      if (scalarInput.damping !== undefined) {
        scalarInput.damping = sanitizeJointNonNegative(scalarInput.damping);
      }
      if (scalarInput.frictionloss !== undefined) {
        scalarInput.frictionloss = sanitizeJointNonNegative(scalarInput.frictionloss);
      }
      if (scalarInput.stiffness !== undefined) {
        scalarInput.stiffness = sanitizeJointNonNegative(scalarInput.stiffness);
      }
      const range = scalarInput.range;
      if (Array.isArray(range) && range.length >= 2) {
        const a = Number(range[0]);
        const b = Number(range[1]);
        if (Number.isFinite(a) && Number.isFinite(b)) {
          scalarInput.range = [Math.min(a, b), Math.max(a, b)];
        }
      }
    }
    const { uuid, joint } = createJointRecord(normalizedInput);
    set((state) =>
      create(state, (draft) => {
        const joints = draft.joints as JointRecordByString;
        // Add to joint map
        joints[uuid] = joint;

        // Add to parent body
        const parentBody = draft.bodies[parentBodyUuid] || null;
        if (parentBody) {
          parentBody.joints = parentBody.joints || [];
          parentBody.joints.push(uuid as JointUUID);
        } else {
          logger.warn(`[addJoint] Parent body ${parentBodyUuid} not found`);
        }
      })
    );
    return uuid as JointUUID;
  }),
});
